import {useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912) — nulls-last, numeric-vs-string and the
// empty-string rule live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {clearSelection} from '../reducers';
import {selectionSelector} from '../selectors';
import {useAppDispatch, useAppSelector} from '../../../hooks/useTypedReactRedux';

import {
    useDeleteRegistrarMutation,
    useRegistrarsInfiniteQuery,
} from './useRegistrarsQuery';
import {useRegistrarsSearch} from './useRegistrarsSearch';
import {Registrar} from '../types';

// ⚠️ `created` DESCENDING — newest first, the ordinary console default, on the
// one key here that is both immutable and set at write time. Never `updated`:
// it moves under the reader. The API's own list is `scanIndexForward: false` on
// `record-type-created-index`, i.e. newest-first already, so the first paint
// does not reshuffle what the server returned.
export const DEFAULT_SORT_STATUS: DataTableSortStatus<Registrar> = {
    columnAccessor: 'created',
    direction: 'desc',
};

/** Which fields the keyword box searches. Exported for its own test. */
export const matchesKeyword = (item: Registrar, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [item.name, item.website, item.description];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase()
                                                                    .includes(needle));
};

// FNP-943: records come from React Query; selection stays in Redux.
// FNP-1292: paging is now TOKEN paging, so the Redux `visibility` slice no
// longer drives the list. `page`/`limit`/`totalPages` are gone from the return
// shape — under a token model there is no page to jump to, no total to divide
// and no page size for a user to choose. `items` and `fetchRecords` are
// unchanged, because six consumers outside this module read them: the dashboard
// widget, the bulk-import upload page and form, the domain-names Add page and
// the three registrar inputs.
export const useRegistrars = () => {

    const dispatch = useAppDispatch();

    const selection = useAppSelector((state) => selectionSelector(state));

    const query = useRegistrarsInfiniteQuery();

    const deleteMutation = useDeleteRegistrarMutation();

    const {keyword} = useRegistrarsSearch();

    // Component state, not the module-scoped store: sort order is how ONE
    // operator is looking at the table right now, unlike the keyword, which is
    // shared across sibling components.
    const [sortStatus, setSortStatus] =
        useState<DataTableSortStatus<Registrar>>(DEFAULT_SORT_STATUS);

    // A type guard, not a bare flatMap — a partial page object would otherwise
    // throw inside a render.
    const items: Registrar[] = (query.data?.pages ?? []).flatMap(
        page => (page && 'items' in page) ? (page.items ?? []) : [],
    ).filter(Boolean);

    const needle = keyword.trim()
                          .toLowerCase();

    // ⚠️ THE SORT AND THE KEYWORD ARE CLIENT-SIDE, over the pages fetched so
    // far. That is a property of token pagination, not an oversight: sorting
    // page 1 does not sort the dataset, and the footer therefore reports a
    // LOADED count rather than a total it cannot know.
    const visibleItems = sortRecords(
        items.filter(item => matchesKeyword(item, needle)),
        sortStatus,
    );

    const fetchRecords = () => {
        query.refetch();
    };

    const deleteRecords = () => {
        if (Object.keys(selection).length > 0) {
            for (const key in selection) {
                deleteMutation.mutate(key);
            }
            // Clear the checkboxes once the deletions are issued — otherwise the
            // selection keeps naming records that no longer exist.
            dispatch(clearSelection());
        }
    };

    return {
        // The loaded set BEFORE the keyword narrows it — the window the
        // footer's count is measured against, and what the non-table consumers
        // read.
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading: query.isLoading,
        // ⚠️ Reachable only because the service re-throws now. While it
        // swallowed and returned undefined, this was permanently false.
        isError: query.isError,
        isFetchingNextPage: query.isFetchingNextPage,
        isReloading: query.isRefetching,
        // ⚠️ DERIVED FROM hasNextPage, NEVER FROM items.length.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchNextPage: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        fetchRecords,
        deleteRecords,
    };
};
